import { AnyAction, combineReducers, Reducer } from "@reduxjs/toolkit";
import { fulfilled, pending, rejected } from "../../app/utils";
import {
    TELIA_PLAY_SELECT_PACKAGE,
    TELIA_PLAY_GET_VAID_FROM_ADDRESS,
    TELIA_PLAY_SEARCH_ADDRESSES,
    TELIA_PLAY_SET_CONTACT_INFO,
    TELIA_PLAY_CUSTOMER_SUBMISSION_STARTED,
    TELIA_PLAY_CUSTOMER_SUBMISSION_FINISHED,
    TELIA_PLAY_CLEAR_PACKAGE_UNAVAILABLE,
    TELIA_PLAY_SET_MOCK_ADDRESS,
    TELIA_PLAY_SELECT_MOCK_ADDRESS,
} from "./actionTypes";
import * as CHECKOUT_AT from "../../checkout/state/actionTypes";
import { FORM_ID, TELIA_PLAY_PACKAGE_NOT_AVAILABLE_STATUS } from "../../../../constants";
import { createReducer as createFormsReducer } from "../../rapidform";
import _get from "lodash/get";
import type { TeliaPlaySubscription } from "../../../../models/state/teliaPlay";
import type { HSSalesPackage } from "../../../../models/server/homeSales";
import type { TeliaPlayResolvedMockAddress } from "../../../../shared/teliaPlayMockAddresses";

const addressSearch = (state: any[] = [], action: AnyAction) => {
    switch (action.type) {
        case pending(TELIA_PLAY_SEARCH_ADDRESSES):
            return [];
        case fulfilled(TELIA_PLAY_SEARCH_ADDRESSES):
            return action.payload.data.items || [];
        case rejected(TELIA_PLAY_SEARCH_ADDRESSES):
            return [];
        default:
            return state;
    }
};

const isPositiveSafeInteger = (value: unknown): value is number =>
    typeof value === "number" && Number.isSafeInteger(value) && value > 0;

const vaidFromAddress = (state: number | "" = "", action: AnyAction) => {
    switch (action.type) {
        case fulfilled(TELIA_PLAY_GET_VAID_FROM_ADDRESS): {
            const technologies: { vaId?: unknown }[] = _get(action, "payload.data.technologies", []) || [];
            return technologies.map((tech) => tech?.vaId).find(isPositiveSafeInteger) ?? "";
        }
        case rejected(TELIA_PLAY_GET_VAID_FROM_ADDRESS):
            return "";
        case fulfilled(TELIA_PLAY_SELECT_MOCK_ADDRESS):
            return isPositiveSafeInteger(action.payload?.vaid) ? action.payload.vaid : "";
        default:
            return state;
    }
};

const selectedPackage = (state: HSSalesPackage | null = null, action: AnyAction): HSSalesPackage | null => {
    switch (action.type) {
        case fulfilled(TELIA_PLAY_SELECT_PACKAGE):
            return _get(action, "payload.data.selectedPackage", null) || null;
        case rejected(TELIA_PLAY_SELECT_PACKAGE):
            return null;
        default:
            return state;
    }
};

const personalDetailsReducer = (state = {}, action: AnyAction) => {
    switch (action.type) {
        case fulfilled(TELIA_PLAY_SET_CONTACT_INFO):
            return {
                ...state,
                phoneNumber: _get(action, "payload.personalDetails.phone", ""),
                email: _get(action, "payload.personalDetails.email", ""),
            };
        default:
            return state;
    }
};

const isBroadbandCustomer = (state = false, action: AnyAction): boolean => {
    switch (action.type) {
        case pending(TELIA_PLAY_GET_VAID_FROM_ADDRESS):
        case rejected(TELIA_PLAY_GET_VAID_FROM_ADDRESS):
        case fulfilled(TELIA_PLAY_SELECT_MOCK_ADDRESS):
            return false;
        case fulfilled(TELIA_PLAY_GET_VAID_FROM_ADDRESS):
            return _get(action, "payload.data.metadata.isActiveCustomer") === true;
        default:
            return state;
    }
};

const isCustomerSubmissionPending = (state = false, action: AnyAction): boolean => {
    switch (action.type) {
        case TELIA_PLAY_CUSTOMER_SUBMISSION_STARTED:
            return true;
        case TELIA_PLAY_CUSTOMER_SUBMISSION_FINISHED:
            return false;
        default:
            return state;
    }
};

const isSelectedPackageUnavailable = (state = false, action: AnyAction): boolean => {
    switch (action.type) {
        case rejected(TELIA_PLAY_SELECT_PACKAGE):
            return _get(action, "payload.httpStatus") === TELIA_PLAY_PACKAGE_NOT_AVAILABLE_STATUS;
        case pending(TELIA_PLAY_SELECT_PACKAGE):
        case fulfilled(TELIA_PLAY_SELECT_PACKAGE):
        case TELIA_PLAY_CLEAR_PACKAGE_UNAVAILABLE:
            return false;
        default:
            return state;
    }
};

const mockAddress = (
    state: TeliaPlayResolvedMockAddress | null = null,
    action: AnyAction,
): TeliaPlayResolvedMockAddress | null => {
    switch (action.type) {
        case TELIA_PLAY_SET_MOCK_ADDRESS:
            return null;
        case fulfilled(TELIA_PLAY_SELECT_MOCK_ADDRESS):
            return action.payload ?? null;
        default:
            return state;
    }
};

const combinedReducer = combineReducers({
    isBroadbandCustomer,
    isCustomerSubmissionPending,
    isSelectedPackageUnavailable,
    selectedSubscription: (state: TeliaPlaySubscription | null = null) => state,
    availableAddresses: addressSearch,
    vaidFromAddress: vaidFromAddress,
    selectedPackage,
    mockAddress,
    personalDetails: personalDetailsReducer,
    [FORM_ID.PERSONAL_DETAILS_TELIA_PLAY]: createFormsReducer(FORM_ID.PERSONAL_DETAILS_TELIA_PLAY),
});

const reducer: Reducer = (state, action) =>
    combinedReducer(action.type === fulfilled(CHECKOUT_AT.CHECKOUT_RESET_ORDER) ? undefined : state, action);

export default reducer;
